import React, { useState, useEffect, useCallback } from 'react';
import { Loader2, Lock, Moon, Sun, FileDown } from 'lucide-react';
import type { PublicShowcaseData } from '../types';
import { getPublicShowcase } from '../services/showcases';
import HeroSection from './sections/HeroSection';
import GallerySection from './sections/GallerySection';
import VideoSection from './sections/VideoSection';
import TourSection from './sections/TourSection';
import FloorPlanSection from './sections/FloorPlanSection';
import DescriptionSection from './sections/DescriptionSection';
import FeaturesSection from './sections/FeaturesSection';
import EpcSection from './sections/EpcSection';
import MapSection from './sections/MapSection';
import DocumentsSection from './sections/DocumentsSection';
import ContactSection from './sections/ContactSection';
import AiAssistantSection from './sections/AiAssistantSection';
import AiAssistant from './AiAssistant';
import { getResourcesForSection, type SectionType } from '../constants';

const SECTION_COMPONENTS: Record<SectionType, React.ComponentType<any>> = {
  hero: HeroSection,
  gallery: GallerySection,
  imagemotion_video: VideoSection,
  standard_video: VideoSection,
  tour_360: TourSection,
  planos_floorplan: FloorPlanSection,
  floorplan_pdf: FloorPlanSection,
  description: DescriptionSection,
  key_features: FeaturesSection,
  epc: EpcSection,
  map_location: MapSection,
  documents: DocumentsSection,
  brochure_download: DocumentsSection,
  contact: ContactSection,
  ai_assistant: AiAssistantSection,
};

interface PublicShowcaseProps {
  slug: string;
}

export default function PublicShowcase({ slug }: PublicShowcaseProps) {
  const [data, setData] = useState<PublicShowcaseData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const [darkMode, setDarkMode] = useState(() => {
    try { return localStorage.getItem('showcase_dark') === '1'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('showcase_dark', darkMode ? '1' : '0'); } catch {}
  }, [darkMode]);

  const loadShowcase = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getPublicShowcase(slug);
      if (!result) {
        setError('Showcase not found');
        return;
      }
      setData(result);

      if (result.showcase.password) {
        const stored = sessionStorage.getItem(`showcase_pass_${result.showcase.id}`);
        if (stored !== result.showcase.password) {
          setPasswordRequired(true);
          setLoading(false);
          return;
        }
      }

      if (result.seo?.custom_meta_title || result.property.property_title) {
        document.title = result.seo?.custom_meta_title || result.property.property_title || result.showcase.title;
      }
    } catch (err) {
      setError('Failed to load showcase');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    loadShowcase();
  }, [loadShowcase]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <Loader2 className="w-8 h-8 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex flex-col bg-white">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-slate-900 mb-2">Property Not Found</h1>
            <p className="text-slate-500">This showcase may have been removed or is no longer available.</p>
          </div>
        </div>
        <footer className="py-5 text-center">
          <a href="https://showcase.rehub.studio" target="_blank" rel="noopener noreferrer" className="inline-block">
            <img src="https://showcase.rehub.studio/Logo-light.png" alt="Showcase" className="h-5 mx-auto object-contain opacity-40 hover:opacity-60 transition-opacity" />
          </a>
        </footer>
      </div>
    );
  }

  if (passwordRequired && data) {
    const handlePasswordSubmit = () => {
      if (passwordInput === data.showcase.password) {
        sessionStorage.setItem(`showcase_pass_${data.showcase.id}`, passwordInput);
        setPasswordRequired(false);
        setPasswordError(false);
      } else {
        setPasswordError(true);
      }
    };

    const pwLogoHeight = ({ small: 'h-8', medium: 'h-12', large: 'h-16', xl: 'h-20' } as Record<string, string>)[data.showcase.logo_size] || 'h-12';
    const propertyTitle = data.property?.property_title || data.showcase.title;

    return (
      <div className="min-h-screen flex flex-col bg-slate-50">
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center max-w-sm px-6">
            {data.showcase.logo_url ? (
              <img src={data.showcase.logo_url} alt={data.showcase.office_name || ''} className={`${pwLogoHeight} mx-auto mb-8 object-contain`} />
            ) : (
              <div className="w-20 h-20 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
                <Lock className="w-9 h-9 text-slate-400" />
              </div>
            )}
            <h1 className="text-2xl font-bold text-slate-900 mb-1">{propertyTitle}</h1>
            {data.showcase.office_name && (
              <p className="text-sm text-slate-400 mb-1">{data.showcase.office_name}</p>
            )}
            <div className="flex items-center justify-center gap-1.5 text-slate-500 mb-6 mt-3">
              <Lock className="w-3.5 h-3.5" />
              <p className="text-sm">This showcase is password protected</p>
            </div>
            <div className="space-y-3">
              <input
                type="password"
                value={passwordInput}
                onChange={(e) => { setPasswordInput(e.target.value); setPasswordError(false); }}
                onKeyDown={(e) => e.key === 'Enter' && handlePasswordSubmit()}
                placeholder="Enter password"
                className={`w-full px-4 py-3 bg-white border rounded-xl text-slate-900 text-center text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all ${
                  passwordError ? 'border-red-500 animate-shake' : 'border-slate-300'
                }`}
                autoFocus
              />
              {passwordError && <p className="text-xs text-red-500">Incorrect password</p>}
              <button
                onClick={handlePasswordSubmit}
                className="w-full px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-sm font-semibold transition-colors"
              >
                View Showcase
              </button>
            </div>
          </div>
        </div>
        <footer className="py-5 text-center">
          <a href="https://showcase.rehub.studio" target="_blank" rel="noopener noreferrer" className="inline-block">
            <img src="https://showcase.rehub.studio/Logo-light.png" alt="Showcase" className="h-5 mx-auto object-contain opacity-40 hover:opacity-60 transition-opacity" />
          </a>
        </footer>
      </div>
    );
  }

  const handleDownloadPdf = async () => {
    if (!data) return;
    setGeneratingPdf(true);
    try {
      const [{ default: PdfBrochure }, { generateAndDownloadPdf }, { generateQrCodeDataUrl }] = await Promise.all([
        import('./PdfBrochure'),
        import('../services/pdf'),
        import('../services/qrcode'),
      ]);
      const { showcase: sc, property: prop, resources: res, template: tmpl } = data;
      const qr = await generateQrCodeDataUrl(sc.slug);
      const brochure = React.createElement(PdfBrochure, {
        showcase: sc,
        property: prop,
        resources: res,
        templateSlug: tmpl.slug,
        qrCodeDataUrl: qr,
        brandColour: sc.primary_brand_colour || '#2563eb',
      });
      await generateAndDownloadPdf(brochure, sc.slug || 'property-brochure');
    } catch (err) {
      console.error('PDF generation failed:', err);
    } finally {
      setGeneratingPdf(false);
    }
  };

  const { showcase, property, resources, template } = data;
  const brandColour = showcase.primary_brand_colour || '#2563eb';
  const logoHeightClass = ({ small: 'h-6', medium: 'h-10', large: 'h-14', xl: 'h-20' } as Record<string, string>)[showcase.logo_size] || 'h-10';
  const sections = showcase.section_order || template.default_section_order;
  const enabledSections = new Set(showcase.enabled_sections || template.default_enabled_sections);
  const showAiAssistant = enabledSections.has('ai_assistant');
  const hasPrintableContent = enabledSections.has('hero') &&
    (enabledSections.has('description') || enabledSections.has('gallery') || enabledSections.has('key_features'));

  const dm = darkMode;

  return (
    <div
      className={`min-h-screen transition-colors duration-300 ${dm ? 'bg-slate-900' : 'bg-white'}`}
      style={{ '--brand-colour': brandColour } as React.CSSProperties}
    >
      {(showcase.logo_url || showcase.office_name) && (
        <header className={`px-6 py-4 border-b transition-colors duration-300 ${dm ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-100'}`}>
          <div className="max-w-5xl mx-auto flex items-center justify-between">
            <div>
              {showcase.logo_url ? (
                <img src={showcase.logo_url} alt={showcase.office_name || 'Agency'} className={`${logoHeightClass} max-w-[280px] object-contain ${dm ? 'brightness-200 invert' : ''}`} />
              ) : (
                <span className={`text-lg font-semibold ${dm ? 'text-white' : 'text-slate-800'}`}>{showcase.office_name}</span>
              )}
            </div>
            <button
              onClick={() => setDarkMode(!dm)}
              className={`p-2 rounded-full transition-colors ${dm ? 'text-yellow-400 hover:bg-slate-800' : 'text-slate-400 hover:bg-slate-100'}`}
              title={dm ? 'Light mode' : 'Dark mode'}
            >
              {dm ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </button>
          </div>
        </header>
      )}

      {!showcase.logo_url && !showcase.office_name && (
        <div className="absolute top-4 right-6 z-30">
          <button
            onClick={() => setDarkMode(!dm)}
            className={`p-2 rounded-full transition-colors ${dm ? 'text-yellow-400 hover:bg-slate-800' : 'text-white/70 hover:bg-white/10'}`}
            title={dm ? 'Light mode' : 'Dark mode'}
          >
            {dm ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>
      )}

      <style>{`
        .showcase-dark .sc-light-bg { background-color: #0f172a !important; }
        .showcase-dark .sc-alt-bg { background-color: #1e293b !important; }
        .showcase-dark .sc-text { color: #f1f5f9 !important; }
        .showcase-dark .sc-text-secondary { color: #94a3b8 !important; }
        .showcase-dark .sc-border { border-color: #334155 !important; }
      `}</style>

      <div className={dm ? 'showcase-dark' : ''}>
        {sections.filter(s => enabledSections.has(s)).map((sectionType, idx) => {
          const SectionComponent = SECTION_COMPONENTS[sectionType];
          if (!SectionComponent || sectionType === 'ai_assistant') return null;

          return (
            <SectionComponent
              key={`${sectionType}-${idx}`}
              showcase={showcase}
              property={property}
              resources={getResourcesForSection(sectionType, resources)}
              brandColour={brandColour}
              darkMode={dm}
            />
          );
        })}
      </div>

      {showcase.logo_url && (
        <div className={`py-8 border-t text-center transition-colors duration-300 ${dm ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
          <img src={showcase.logo_url} alt={showcase.office_name || 'Agency'} className={`${logoHeightClass} mx-auto ${dm ? 'brightness-200 invert' : ''}`} />
        </div>
      )}

      <footer className="py-6 bg-slate-900 text-center">
        <a href="https://showcase.rehub.studio" target="_blank" rel="noopener noreferrer" className="inline-block">
          <img src="https://showcase.rehub.studio/Logo.png" alt="Showcase" className="h-5 object-contain opacity-50 hover:opacity-70 transition-opacity" />
        </a>
      </footer>

      {hasPrintableContent && (
        <button
          onClick={handleDownloadPdf}
          disabled={generatingPdf}
          className={`fixed z-40 flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg text-sm font-medium transition-all ${
            showAiAssistant ? 'bottom-6 left-6' : 'bottom-6 right-6'
          } ${dm
            ? 'bg-slate-700 text-slate-200 hover:bg-slate-600'
            : 'bg-white text-slate-700 hover:bg-slate-50 border border-slate-200'
          } disabled:opacity-60`}
          title="Download PDF Brochure"
        >
          {generatingPdf ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
          {generatingPdf ? 'Generating...' : 'Download Brochure'}
        </button>
      )}

      {showAiAssistant && (
        <AiAssistant
          showcaseId={showcase.id}
          property={property}
          resources={resources}
        />
      )}
    </div>
  );
}
